import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ShieldWarning, ArrowSquareOut, CalendarBlank } from "@phosphor-icons/react";

export default function SorvVerificationDialog({ open, onOpenChange, staff, onCreated }) {
  const [config, setConfig] = useState(null);
  const [searchedAt, setSearchedAt] = useState(new Date());
  const [reviewerName, setReviewerName] = useState("");
  const [jurisdiction, setJurisdiction] = useState("National (NSOPW)");
  const [result, setResult] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/sorv/config").then((r) => setConfig(r.data));
    }
  }, [open]);

  const openNsopw = () => {
    window.open(config?.search_url || "https://www.nsopw.gov/", "_blank", "noopener,noreferrer");
    setConfirmed(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reviewerName.trim() || !jurisdiction.trim() || !result) {
      toast.error("Reviewer name, jurisdiction and result are required");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/staff/${staff.id}/sorv-verifications`, {
        searched_at: format(searchedAt, "yyyy-MM-dd"),
        reviewer_name: reviewerName.trim(),
        jurisdiction: jurisdiction.trim(),
        result,
        notes,
      });
      toast.success("Verification logged in audit trail");
      setReviewerName(""); setNotes(""); setResult(""); setConfirmed(false);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to log verification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-white/10 text-slate-50 max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="sorv-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Sex Offender Registry Verification</DialogTitle>
          <DialogDescription className="text-slate-400">
            Log the search you performed on the official NSOPW registry for <span className="text-slate-200 font-semibold">{staff?.name}</span>.
          </DialogDescription>
        </DialogHeader>

        {/* Strong disclaimer */}
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 flex gap-3" data-testid="sorv-legal-warning">
          <ShieldWarning size={20} weight="bold" className="text-rose-400 shrink-0 mt-0.5" />
          <div className="text-xs text-rose-200 leading-relaxed">
            <span className="font-semibold text-rose-100">Important legal notice.</span>{" "}
            AuditReady Sports does NOT scrape or copy data from any government registry.
            A name match alone is <span className="font-bold uppercase">never</span> grounds for adverse action.
            Manual review, identity confirmation and appropriate legal counsel are required before any employment decision.
          </div>
        </div>

        {/* Step 1: Open NSOPW */}
        <div className="rounded-xl border border-white/10 bg-slate-950 p-4">
          <div className="font-label text-orange-400 mb-2">STEP 1 · SEARCH NSOPW</div>
          <p className="text-sm text-slate-400 mb-3">
            Open the official National Sex Offender Public Website search in a new tab, then complete your search using the person's information.
          </p>
          <Button
            type="button"
            data-testid="open-nsopw-button"
            onClick={openNsopw}
            className="w-full bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
          >
            <ArrowSquareOut size={16} weight="bold" className="mr-2" />
            Search National Sex Offender Registry
          </Button>
          {confirmed && (
            <p className="text-xs text-emerald-300 mt-2">
              ✓ NSOPW opened in a new tab. Complete your search there, then fill out the form below.
            </p>
          )}
        </div>

        {/* Step 2: Log the outcome */}
        <form onSubmit={submit} className="space-y-4">
          <div className="font-label text-orange-400 -mb-2">STEP 2 · DOCUMENT RESULT</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-label text-slate-400">SEARCH DATE *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="sorv-date-picker"
                    className="w-full mt-1 justify-start bg-slate-950 border-white/10 text-slate-100 hover:bg-slate-900 hover:text-slate-50"
                  >
                    <CalendarBlank size={16} className="mr-2 text-slate-400" />
                    {format(searchedAt, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-900 border-white/10">
                  <Calendar mode="single" selected={searchedAt} onSelect={(d) => d && setSearchedAt(d)} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="font-label text-slate-400">JURISDICTION *</Label>
              <Input
                data-testid="sorv-jurisdiction-input"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                placeholder="National (NSOPW), state, county…"
                className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
                required
              />
            </div>
          </div>

          <div>
            <Label className="font-label text-slate-400">REVIEWER NAME *</Label>
            <Input
              data-testid="sorv-reviewer-input"
              value={reviewerName}
              onChange={(e) => setReviewerName(e.target.value)}
              placeholder="Compliance admin who performed the search"
              className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
              required
            />
          </div>

          <div>
            <Label className="font-label text-slate-400">RESULT *</Label>
            <Select value={result} onValueChange={setResult}>
              <SelectTrigger data-testid="sorv-result-select" className="bg-slate-950 border-white/10 mt-1">
                <SelectValue placeholder="Choose an outcome" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
                {(config?.results || []).map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">
              Use "Possible match" for any potential hit — never mark someone ineligible on a name match alone.
            </p>
          </div>

          <div>
            <Label className="font-label text-slate-400">NOTES</Label>
            <Textarea
              data-testid="sorv-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Manual-review steps, identifying details reviewed, any escalation…"
              className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1 min-h-[70px]"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-sorv-button"
              disabled={loading}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold"
            >
              {loading ? "Saving…" : "Log verification"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
